import { helpers } from 'vuelidate/lib/validators'
import { withParams } from 'vuelidate/lib/validators/common'
import {IBAN_LENGTHS} from "@base/configs";

export const alphaSpaces = helpers.regex('alpha', /^[a-zA-ZæøåÆØÅ\s]*$/)

export const marketIdValidator = value => {
  return /^[a-zæøå\d\-_]*$/i.test(value)
}

export const productPrefixValidator = value => {
  return /^([a-z]){3}$/.test(value)
}

export const flexposCompanyIdValidator = value => {
  return /^([0-9]){6}$/.test(value)
}

export const allAlphaLettersValidator = value => {
  return /^[a-zA-ZæøåÆØÅ]*$/.test(value)
}

export const noQuotesValidator = value => {
  if (value)
    return /^[^'"`]*$/.test(value)
  return true
}

export const noSpecialHtmlCharsValidator = value => {
  if (value)
    return /^[^'"<>\\=]*$/.test(value)
  return true
}

// Code 128 barcodes can only encode printable ASCII (space..tilde).
export const asciiOnlyValidator = value => {
  if (value)
    return /^[ -~]*$/.test(value)
  return true
}

// Mirrors the FlexPOS till: an alphanumeric code of 8+ must be a GTIN-8, UPC-A or EAN-13 with a valid check digit.
export const flexposBarcodeValidator = value => {
  if (!value || !/^[0-9a-zA-Z]+$/.test(value) || value.length <= 7) return true
  if (![8, 12, 13].includes(value.length) || !/^\d+$/.test(value)) return false
  const digits = value.split('').map(Number)
  const checkDigit = digits.pop()
  const weighted = digits
    .reverse()
    .reduce((sum, digit, position) => sum + digit * (position % 2 === 0 ? 3 : 1), 0)
  return (10 - (weighted % 10)) % 10 === checkDigit
}

export const instagramUrlValidator = value => {
  if (value)
    return /^(http(s)?:\/\/)?(www\.)?instagram\.com\/[a-z\d\-_.]{1,255}(\/)?$/i.test(value)
  return true
}

export const facebookUrlValidator = value => {
  if (value)
    return /^(http(s)?:\/\/)?(www\.)?(facebook\.com|fb\.me)\/[a-z\d\-_.?=]{5,255}(\/)?$/i.test(value)
  return true
}

export const urlValidator = value => {
  if (value)
    return /^(http(s)?:\/\/)?(www\.)?([a-zæøåÆØÅ\d-]{1,63}\.)*[a-zæøåÆØÅ\d-]{1,255}\.[a-z]{2,6}(\/)?$/i.test(value)
  return true
}

export const decimalValidator = value => {
  if (value) {
    // eslint-disable-next-line no-undef
    if (LANGUAGE_CODE === 'en')
      return /^(?!0{2,}\.)(?=.{1,17}(\.|$))\d{1,3}((,)?\d{3})*(\.\d{1,2})?$/.test(value)
    else
      return /^(?!0{2,},)(?=.{1,17}(\.|$))\d{1,3}((\.)?\d{3})*(,\d{1,2})?$/.test(value)
  }
  return true
}

export const decimalWithNegativeValidator = value => {
  if (value) {
    // eslint-disable-next-line no-undef
    if (LANGUAGE_CODE === 'en')
      return /^-?(?!0+\.00)(?=.{1,17}(\.|$))\d{1,3}((,)?\d{3})*(\.\d{1,2})?$/.test(value)
    else
      return /^-?(?!0+\.00)(?=.{1,17}(\.|$))\d{1,3}((\.)?\d{3})*(,\d{1,2})?$/.test(value)
  }
  return true
}

export const betweenValidator = (min, max) => {
  return withParams({
    type: 'betweenValidator',
    min: min,
    max: max,
  }, value => {
    if (value) {
      const number = parseFloat(value.replace(/[.,](?=\d*[.,])/g,''))
      return min <= number && max >= number
    }
    return true
  })
}

const mod97 = (iban) => {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, c => c.charCodeAt(0) - 55);

  let remainder = 0;
  for (let i = 0; i < numeric.length; i++) {
    remainder = (remainder * 10 + parseInt(numeric[i])) % 97;
  }
  return remainder;
};

export const ibanValidator = value => {
  if (!value) return true;

  // Strip spaces to match the backend, so a pasted grouped IBAN still validates.
  const iban = value.replace(/\s/g, '').toUpperCase();
  const country = iban.slice(0, 2);

  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(iban)) return false;
  if (!IBAN_LENGTHS[country]) return false;
  if (iban.length !== IBAN_LENGTHS[country]) return false;

  return mod97(iban) === 1;
};


export const labelsToPrintValidator = value => {
  if (value)
    return /^[0-9]+(?:[-,][0-9]+)*$/g.test(value)
  return true
}

export const UUIDValidator = value => {
  if (value)
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/g.test(value)
  return true
}